// ============================================================================
// GST DUE-DATE EXTENSIONS (ADR-0021 §6, §7)
// ============================================================================
// The notifications that moved a deadline, and what each one reached.
//
// Distinct from the due-date RULES screen, and the distinction is the whole
// design: a rule is permanent and forward-looking (the 20th became the 22nd),
// while an extension is a one-off stamped onto specific obligations. Folding
// them together would mean either rewriting history every time a rule changed,
// or losing the citation a late-fee notice has to be answered with.
//
// Recording, previewing and applying are three separate steps. A notification
// is usually read before anyone is certain it has been understood, and stamping
// hundreds of deadlines should follow a preview rather than happen as a side
// effect of typing a reference.
//
// Follows the mandatory screen blueprint in prima-web/CLAUDE.md.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { gstExtensionService } from 'api/services/gstExtensionService';
import GstExtensionModal from 'components/modals/modules/gst/GstExtensionModal';
import GstExtensionPreviewModal from 'components/modals/modules/gst/GstExtensionPreviewModal';
import type { ObligationExtension } from 'api/types/obligationExtension.types';

type ViewMode = 'list' | 'grid';

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const FORM_FILTER_OPTIONS = [
  { value: '', label: 'All returns' },
  { value: 'GSTR1', label: 'GSTR-1' },
  { value: 'GSTR3B', label: 'GSTR-3B' },
  { value: 'IFF', label: 'IFF' },
  { value: 'PMT06', label: 'PMT-06' },
];

const APPLIED_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Recorded, not yet applied' },
  { value: 'applied', label: 'Applied' },
];

const SCHEME_FILTER_OPTIONS = [
  { value: '', label: 'All frequencies' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QRMP', label: 'QRMP' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const periodLabel = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

/** Null state codes mean "every state" — a real scope, not a missing value. */
const scopeLabel = (e: ObligationExtension): string => {
  const where = e.stateCodes && e.stateCodes.length > 0
    ? `${e.stateCodes.length} state(s)`
    : 'Nationwide';
  const who = e.schemeCode ? e.schemeCode : 'all schemes';
  return `${where} · ${who}`;
};

const GstExtensionsScreen: React.FC<Props> = ({ moduleIcon, tabId, moduleName, allowedActions }) => {
  void tabId; void moduleName;

  const canAdd = !allowedActions || allowedActions.includes('add');
  const canApply = !allowedActions || allowedActions.includes('edit');

  const [rows, setRows] = useState<ObligationExtension[]>([]);
  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [gridPage, setGridPage] = useState(0);
  const gridPageSize = 12;

  // Applied filters.
  const [formFilter, setFormFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [schemeFilter, setSchemeFilter] = useState('');

  // Drafts — pushed to the applied values on Apply, per the blueprint.
  const [draftForm, setDraftForm] = useState('');
  const [draftApplied, setDraftApplied] = useState('');
  const [draftScheme, setDraftScheme] = useState('');

  const [recordOpen, setRecordOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState<ObligationExtension | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ObligationExtension | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await gstExtensionService.getAll());
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not load GST extensions.',
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Filtering and search are client-side here, unlike the paged master screens.
  // Extensions are notifications: a firm sees a handful a year, and the whole
  // set is already loaded.
  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((e) => {
      if (formFilter && e.formCode !== formFilter) return false;
      if (schemeFilter && (e.schemeCode ?? '') !== schemeFilter) return false;
      if (appliedFilter === 'applied' && !e.appliedAt) return false;
      if (appliedFilter === 'pending' && e.appliedAt) return false;
      if (!q) return true;
      return [e.notificationRef, e.formCode, e.schemeCode, e.scopeNote, e.createdByName,
              (e.stateCodes ?? []).join(' ')]
        .some((f) => (f ?? '').toLowerCase().includes(q));
    });
  }, [rows, searchQuery, formFilter, schemeFilter, appliedFilter]);

  const handlePreview = useCallback((e: ObligationExtension) => {
    setSelected(e);
    setPreviewOpen(true);
  }, []);

  const handleRevokeConfirmed = useCallback(async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    try {
      const cleared = await gstExtensionService.revoke(revokeTarget.id);
      notificationService.toast.success(
        `${cleared} obligation(s) returned to their statutory due date.`,
      );
      setRevokeTarget(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not revoke the extension.',
      );
    } finally {
      setIsRevoking(false);
    }
  }, [revokeTarget, load]);

  const getRowActions = useCallback((row: ObligationExtension): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'preview',
        label: row.appliedAt ? 'Preview / re-apply' : 'Preview & apply',
        icon: <IconSystem.eye size={16} />,
        onClick: () => handlePreview(row),
      },
    ];
    if (canApply && row.appliedAt) {
      items.push({
        id: 'revoke',
        label: 'Revoke',
        icon: <IconSystem.xCircle size={16} />,
        onClick: () => setRevokeTarget(row),
        variant: 'danger',
      });
    }
    return items;
  }, [canApply, handlePreview]);

  const columns: SyutGridColumn<ObligationExtension>[] = useMemo(() => [
    {
      id: 'notificationRef',
      header: 'Notification',
      accessorKey: 'notificationRef',
      size: 200,
      enableSorting: true,
    },
    { id: 'formCode', header: 'Return', accessorKey: 'formCode', size: 100, enableSorting: true },
    {
      id: 'periodStart',
      header: 'Period',
      accessorKey: 'periodStart',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => periodLabel(row.original.periodStart),
    },
    {
      id: 'newDueDate',
      header: 'Extended to',
      accessorKey: 'newDueDate',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => <strong>{formatDate(row.original.newDueDate)}</strong>,
    },
    {
      id: 'scope',
      header: 'Scope',
      accessorFn: (row: ObligationExtension) => scopeLabel(row),
      size: 190,
      enableSorting: true,
      cell: ({ row }) => (
        <span title={(row.original.stateCodes ?? []).join(', ') || 'Every state'}>
          {scopeLabel(row.original)}
        </span>
      ),
    },
    {
      id: 'applied',
      header: 'Applied',
      accessorKey: 'appliedAt',
      size: 190,
      enableSorting: true,
      // Never-applied and applied-but-matched-nothing are different facts, and
      // the one someone asks about is almost always the second.
      cell: ({ row }) => {
        const e = row.original;
        if (!e.appliedAt) return <span className="badge badge-inactive">Not applied</span>;
        if ((e.appliedCount ?? 0) === 0) {
          return (
            <span className="badge badge-inactive" title="Applied, but nothing was in scope">
              Matched nothing
            </span>
          );
        }
        return (
          <span className="badge badge-active" title={formatDate(e.appliedAt)}>
            {e.appliedCount} on {formatDate(e.appliedAt)}
          </span>
        );
      },
    },
    {
      id: 'issuedOn',
      header: 'Issued',
      accessorKey: 'issuedOn',
      size: 120,
      enableSorting: true,
      cell: ({ row }) => formatDate(row.original.issuedOn),
    },
  ], []);

  const pendingCount = useMemo(() => rows.filter((e) => !e.appliedAt).length, [rows]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (canAdd) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={16} />,
        label: 'Record',
        tooltip: 'Record a notification',
        variant: 'primary' as const,
        onClick: () => setRecordOpen(true),
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      variant: 'icon' as const,
      onClick: () => { void load(); },
    });
    return buttons;
  }, [canAdd, load]);

  const activeFilterCount = useMemo(
    () => (formFilter ? 1 : 0) + (appliedFilter ? 1 : 0) + (schemeFilter ? 1 : 0),
    [formFilter, appliedFilter, schemeFilter],
  );

  const applyFilters = useCallback(() => {
    setFormFilter(draftForm);
    setAppliedFilter(draftApplied);
    setSchemeFilter(draftScheme);
    setGridPage(0);
    setFilterOpen(false);
  }, [draftForm, draftApplied, draftScheme]);

  const clearFilters = useCallback(() => {
    setDraftForm(''); setDraftApplied(''); setDraftScheme('');
    setFormFilter(''); setAppliedFilter(''); setSchemeFilter('');
    setGridPage(0);
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="GST Due-Date Extensions"
        description="Notifications that moved a GST deadline. Recording one changes nothing until it is previewed and applied."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.clock size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search notification, return, state…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Card view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {/* A recorded extension that was never applied has moved no
                deadline — which is exactly what someone assumes it did. */}
            {!loading && pendingCount > 0 && (
              <div
                style={{
                  border: '1px solid #f59e0b', background: 'var(--bg-secondary)',
                  borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: 8,
                  fontSize: '0.8125rem', color: 'var(--text-secondary)', flexShrink: 0,
                }}
              >
                <strong style={{ color: 'var(--text-primary)' }}>
                  {pendingCount} extension(s) recorded but not applied
                </strong>{' '}
                — no deadline has moved for these. Open one to preview its scope and apply it.
              </div>
            )}

            {viewMode === 'list' && (
              <SyutGrid
                data={visibleRows as any}
                columns={columns as any}
                enableSorting
                enablePagination
                pageSize={25}
                rowActions={getRowActions as any}
                emptyMessage={loading ? 'Loading…' : 'No extensions recorded.'}
              />
            )}

            {viewMode === 'grid' && (() => {
              const totalPages = Math.max(1, Math.ceil(visibleRows.length / gridPageSize));
              const safePage = Math.min(gridPage, totalPages - 1);
              const paged = visibleRows.slice(safePage * gridPageSize, (safePage + 1) * gridPageSize);
              return (
                <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      padding: '1rem', display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
                    }}
                  >
                    {paged.map((e) => (
                      <div
                        key={e.id}
                        style={{
                          border: '1px solid var(--border-color)', borderRadius: 8,
                          padding: '0.875rem', background: 'var(--bg-primary)',
                          display: 'flex', flexDirection: 'column', gap: 6,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                            {e.notificationRef}
                          </strong>
                          <ActionMenu items={getRowActions(e)} />
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {e.formCode} · {periodLabel(e.periodStart)} → <strong style={{ color: 'var(--text-primary)' }}>{formatDate(e.newDueDate)}</strong>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {scopeLabel(e)}
                        </div>
                        <div>
                          {e.appliedAt
                            ? (
                              <span className={(e.appliedCount ?? 0) === 0 ? 'badge badge-inactive' : 'badge badge-active'}>
                                {(e.appliedCount ?? 0) === 0
                                  ? 'Matched nothing'
                                  : `${e.appliedCount} applied ${formatDate(e.appliedAt)}`}
                              </span>
                            )
                            : <span className="badge badge-inactive">Not applied</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pagination-footer">
                    <button
                      className="btn btn-secondary"
                      disabled={safePage === 0}
                      onClick={() => setGridPage((p) => Math.max(0, p - 1))}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      Page {safePage + 1} of {totalPages}
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={safePage >= totalPages - 1}
                      onClick={() => setGridPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Return"
                  options={FORM_FILTER_OPTIONS}
                  value={draftForm}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setDraftForm((v as string) || '')}
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Applied"
                  options={APPLIED_FILTER_OPTIONS}
                  value={draftApplied}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setDraftApplied((v as string) || '')}
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Filing frequency"
                  options={SCHEME_FILTER_OPTIONS}
                  value={draftScheme}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setDraftScheme((v as string) || '')}
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  Matches the extension's own frequency scope — "All frequencies" includes
                  the ones that name no frequency at all.
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <GstExtensionModal
        isOpen={recordOpen}
        onClose={() => setRecordOpen(false)}
        onSaved={(created) => {
          void load();
          // Straight into the preview: recording without checking the scope is
          // the mistake this screen exists to prevent.
          setSelected(created);
          setPreviewOpen(true);
        }}
      />

      <GstExtensionPreviewModal
        isOpen={previewOpen}
        extension={selected}
        canApply={canApply}
        onClose={() => { setPreviewOpen(false); setSelected(null); }}
        onApplied={() => { void load(); }}
      />

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke this extension?"
        message={
          `Every obligation carrying ${revokeTarget?.notificationRef ?? 'this notification'} `
          + 'returns to its statutory due date. Use this when the date was recorded wrongly — '
          + 'the "may only move later" rule means a mistyped date cannot be corrected in place.'
        }
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        variant="danger"
        loading={isRevoking}
        loadingMessage="Revoking…"
        onConfirm={() => { void handleRevokeConfirmed(); }}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
};

export default GstExtensionsScreen;
